// ─── Plantilla: recibo de compra para el CLIENTE del negocio ─────────────────
//
// Lo dispara el cajero desde el ticket tras cobrar (CarritoPOS → emails.js →
// emailTicketCliente). A diferencia de las plantillas de emailTemplates.js
// (mezo → dueño del negocio), aquí el remitente "visual" es EL NEGOCIO: el
// header lleva su nombre, el pie sus datos de contacto, y mezo solo firma
// discreto abajo. Mismo shell table-based y paleta del layout() maestro para
// consistencia de marca (Fraunces italic + DM Sans, tinta/oro/crema).
//
// SEGURIDAD: todo texto que viene del negocio o del cajero (nombre del
// producto, nota "sin cebolla", nombre del negocio, dirección…) pasa por
// escapeHtml. Un producto llamado `<img src=x onerror=…>` o una nota con
// `</td>` no debe romper la plantilla ni inyectar markup en el correo.
//
// MODO OSCURO: este correo lo abre el CLIENTE del negocio, casi siempre en el
// celular, que es justo donde Gmail invierte los colores si no le damos una
// versión oscura propia. Las reglas viven en `emailDarkMode.js` y las comparten
// los 7 correos — ahí está explicado el porqué y sus límites.

import { ESTILO_MODO_OSCURO, META_MODO_OSCURO } from './emailDarkMode';

export function escapeHtml(v) {
  return String(v ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

const copFmt = new Intl.NumberFormat('es-CO', {
  style: 'currency', currency: 'COP', maximumFractionDigits: 0,
});
const cop = (n) => copFmt.format(Number(n) || 0);

// La paleta oscura y sus reglas viven en `emailDarkMode.js` — una sola
// fuente de verdad para los 7 correos, no una copia por plantilla.

// negocio: campos de fromNegocio (api.js): name, razonSocial, nit, address,
//          direccionFiscal, phone, telefono, pieFactura.
// orden:   { lineas[{name|nombre, quantity|cantidad, subtotal, unitPrice|precio,
//            nota, mods?}], subtotal, propina{porcentaje,monto},
//            canje{nombre,monto}, total, metodoLabel, numero, mesaNumero,
//            cajero, fechaStr }  — ya normalizado por emails.js.
//
// OJO con `orden.cajero`: emails.js ya lo pasa por nombreQueAtendio(), así que
// nunca llega un correo aquí. Si algún día alguien arma el payload a mano, esa
// función es la que hay que usar — no el valor crudo del POS.
export function templateReciboCliente({ negocio, orden }) {
  const e = escapeHtml;
  const nombreNegocio = negocio?.razonSocial || negocio?.name || 'mezo';
  const direccion = negocio?.direccionFiscal || negocio?.address || null;
  const telefono  = negocio?.telefono || negocio?.phone || null;
  const nit       = negocio?.nit || null;

  const filas = (orden?.lineas ?? []).map((l) => {
    const cant   = l.quantity ?? l.cantidad ?? 1;
    const nombre = l.name ?? l.nombre ?? 'Producto';
    const sub    = l.subtotal ?? (Number(l.unitPrice ?? l.precio ?? 0) * Number(cant));
    const listaMods = l.mods ?? l._mods;
    // Modificadores sueltos (si la línea los trae aparte) + nota de preparación.
    const mods = Array.isArray(listaMods) && listaMods.length
      ? `<div class="mz-tenue" style="color:#7A6A58;font-size:12px;">+ ${listaMods.map((m) => e(m?.nombre ?? m)).join(', ')}</div>`
      : '';
    const nota = l.nota
      ? `<div class="mz-tenue" style="color:#7A6A58;font-size:12px;font-style:italic;">${e(l.nota)}</div>`
      : '';
    return `
      <tr>
        <td class="mz-texto mz-linea" style="padding:9px 0;border-bottom:1px solid #E8DDC2;color:#2B2420;font-size:14px;vertical-align:top;">
          <span class="mz-oro" style="color:#B8802F;font-weight:600;">${e(cant)}×</span> ${e(nombre)}${mods}${nota}
        </td>
        <td align="right" class="mz-texto mz-linea" style="padding:9px 0;border-bottom:1px solid #E8DDC2;color:#2B2420;font-size:14px;white-space:nowrap;vertical-align:top;">${e(cop(sub))}</td>
      </tr>`;
  }).join('');

  const filaTotal = (label, valor, fuerte = false) => `
    <tr>
      <td class="${fuerte ? 'mz-texto' : 'mz-tenue'}" style="padding:3px 0;color:${fuerte ? '#2B2420' : '#7A6A58'};font-size:${fuerte ? '16px' : '13px'};font-weight:${fuerte ? '700' : '400'};">${label}</td>
      <td align="right" class="${fuerte ? 'mz-oro' : 'mz-texto'}" style="padding:3px 0;color:${fuerte ? '#B8802F' : '#2B2420'};font-size:${fuerte ? '20px' : '13px'};font-weight:${fuerte ? '700' : '500'};white-space:nowrap;">${valor}</td>
    </tr>`;

  const totales = [
    orden?.subtotal != null ? filaTotal('Subtotal', e(cop(orden.subtotal))) : '',
    orden?.canje?.monto > 0
      ? filaTotal(`Canje${orden.canje.nombre ? ` · ${e(orden.canje.nombre)}` : ''}`, `−${e(cop(orden.canje.monto))}`)
      : '',
    orden?.propina?.monto > 0
      ? filaTotal(`Propina${orden.propina.porcentaje ? ` (${e(orden.propina.porcentaje)}%)` : ''}`, e(cop(orden.propina.monto)))
      : '',
    filaTotal('Total', e(cop(orden?.total)), true),
  ].join('');

  // Metadatos de la venta: orden, mesa, método, quién atendió.
  const fuerte = (t) => `<b class="mz-texto" style="color:#2B2420;">${t}</b>`;
  const meta = [
    orden?.numero ? `Orden ${fuerte(`#${e(orden.numero)}`)}` : '',
    orden?.mesaNumero != null ? `Mesa ${fuerte(e(orden.mesaNumero))}` : '',
    orden?.metodoLabel ? `Pago: ${fuerte(e(orden.metodoLabel))}` : '',
    orden?.cajero ? `Te atendió: ${fuerte(e(orden.cajero))}` : '',
  ].filter(Boolean).join('&nbsp;&nbsp;·&nbsp;&nbsp;');

  const pieNegocio = [
    nit ? `NIT ${e(nit)}` : '',
    direccion ? e(direccion) : '',
    telefono ? `Tel. ${e(telefono)}` : '',
  ].filter(Boolean).join(' · ');

  return `<!DOCTYPE html>
<html lang="es">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <meta name="x-apple-disable-message-reformatting" />
  ${META_MODO_OSCURO}
  <title>Recibo de ${e(nombreNegocio)}</title>
  <!--[if mso]><style>* { font-family: Georgia, serif !important; }</style><![endif]-->
  <link href="https://fonts.googleapis.com/css2?family=Fraunces:ital,opsz,wght@0,9..144,500;0,9..144,600;1,9..144,500;1,9..144,600&family=DM+Sans:wght@400;500;600&display=swap" rel="stylesheet" />
  ${ESTILO_MODO_OSCURO}
</head>
<body class="mz-fondo" style="margin:0;padding:0;background:#E8E4DB;font-family:'DM Sans',-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif;color:#2B2420;-webkit-font-smoothing:antialiased;">
<div style="display:none;max-height:0;overflow:hidden;mso-hide:all;font-size:1px;line-height:1px;color:#E8E4DB;">Tu recibo de ${e(nombreNegocio)} · ${e(cop(orden?.total))}</div>
<table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0" class="mz-fondo" style="background:#E8E4DB;padding:24px 12px;">
  <tr>
    <td align="center">
      <table role="presentation" width="560" cellpadding="0" cellspacing="0" border="0" class="mz-tarjeta" style="background:#F4ECD8;border-radius:14px;overflow:hidden;max-width:560px;width:100%;">

        <!-- Header banda oscura: el NEGOCIO es el protagonista. Sin clase de
             modo oscuro a propósito — ya es tinta, y en oscuro se funde con la
             tarjeta como una sola pieza. -->
        <tr><td style="background:#080706;padding:22px 36px;border-bottom:1px solid #2A2520;">
          <table width="100%" cellpadding="0" cellspacing="0" border="0">
            <tr>
              <td><span style="font-family:'Fraunces',Georgia,serif;font-style:italic;font-weight:600;font-size:24px;color:#C8903F;letter-spacing:-0.03em;">${e(nombreNegocio)}</span></td>
              <td align="right"><span style="font-size:10px;letter-spacing:0.24em;text-transform:uppercase;color:#B8A88E;">Recibo de compra</span></td>
            </tr>
          </table>
          <div style="color:#B8A88E;font-size:12px;margin-top:6px;">${e(orden?.fechaStr ?? '')}</div>
        </td></tr>

        <!-- Meta -->
        ${meta ? `<tr><td class="mz-panel mz-tenue" style="padding:16px 36px 0;background:#F4ECD8;font-size:12px;color:#7A6A58;line-height:1.6;">${meta}</td></tr>` : ''}

        <!-- Líneas -->
        <tr><td class="mz-panel" style="padding:14px 36px 4px;background:#F4ECD8;">
          <table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0">${filas}</table>
        </td></tr>

        <!-- Totales -->
        <tr><td class="mz-panel" style="padding:10px 36px 22px;background:#F4ECD8;">
          <table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0">${totales}</table>
          ${negocio?.pieFactura ? `<p class="mz-tenue" style="margin:16px 0 0;color:#7A6A58;font-size:12px;line-height:1.5;">${e(negocio.pieFactura)}</p>` : ''}
          <p class="mz-texto" style="margin:14px 0 0;color:#3A322A;font-size:13px;line-height:1.55;">¡Gracias por tu compra! Te esperamos de vuelta.</p>
        </td></tr>

        <!-- Footer: datos del negocio + firma mezo/TemeT -->
        <tr><td class="mz-pie mz-tenue mz-borde" style="background:#EBE2C9;padding:16px 36px 20px;border-top:1px solid #E8DDC2;font-size:11px;color:#7A6A58;line-height:1.6;">
          ${pieNegocio ? `<div class="mz-texto" style="color:#2B2420;font-weight:500;margin-bottom:4px;">${e(nombreNegocio)} · ${pieNegocio}</div>` : ''}
          <div class="mz-tenue" style="font-size:10.5px;color:#7A6A58;">Enviado con <span class="mz-texto" style="font-family:'Fraunces',serif;font-style:italic;font-size:12px;color:#2B2420;">mezo</span>, el punto de venta de ${e(nombreNegocio)} · <a href="https://mezopos.com" class="mz-oro" style="color:#B8802F;text-decoration:none;">mezopos.com</a></div>
          <div class="mz-tenue" style="font-size:10.5px;color:#8A7A66;margin-top:3px;">mezo es un producto de <b class="mz-texto" style="color:#2B2420;font-weight:600;">TemeT</b> · Bogotá, Colombia</div>
        </td></tr>

      </table>
    </td>
  </tr>
</table>
</body>
</html>`;
}
